import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/HomePage.css';
import carapuceCard from '../assets/images/cards/carapuce1.webp';
import carapuceCardFallback from '../assets/images/cards/carapuce1.jpg';
import spectrumCard from '../assets/images/cards/spectrum.webp';
import spectrumCardFallback from '../assets/images/cards/spectrum.jpg';
import { useLanguage } from '../i18n/LanguageContext';
import Picture from '../components/Picture';
import { CardsThreeIcon, YarnIcon } from '@phosphor-icons/react';

const REVIEWS_ENDPOINT = '/.netlify/functions/reviews-public';
const PRODUCTS_PUBLIC_ENDPOINT = '/.netlify/functions/products-public';
const MAX_FEATURED_REVIEWS = 3;
const HERO_SHOT_CLASSES = [
    'mosaic-item mosaic-main',
    'mosaic-item mosaic-tall',
    'mosaic-item mosaic-wide',
    'mosaic-item',
    'mosaic-item',
    'mosaic-item'
];

const buildNumberFormatter = (language, options) => {
    const locale = language === 'fr' ? 'fr-FR' : 'en-US';
    return new Intl.NumberFormat(locale, options);
};

const StarIcon = ({ className }) => (
    <svg
        className={className}
        viewBox='0 0 24 24'
        aria-hidden='true'
        focusable='false'
    >
        <path d='M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z' />
    </svg>
);

const StarRow = ({ value, label, size = 18 }) => {
    const filled = Math.round(value);
    const stars = Array.from({ length: 5 }, (_, index) => (
        <StarIcon
            key={index}
            className={`star-icon${index < filled ? ' is-filled' : ''}`}
        />
    ));

    return (
        <div
            className='star-row'
            style={{
                '--star-size': `${size}px`
            }}
            aria-label={label}
        >
            {stars}
        </div>
    );
};

const shuffleArray = (items) => {
    const copy = [...items];
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
};

const pickFeaturedReviews = (items) => {
    const filtered = items.filter((review) => Number(review.rating) >= 4);
    const shuffled = shuffleArray(filtered);
    if (!shuffled.length) {
        return [];
    }

    const maxCount = Math.min(MAX_FEATURED_REVIEWS, shuffled.length);
    const count = maxCount >= 3 ? (Math.random() > 0.5 ? 3 : 2) : maxCount;
    return shuffled.slice(0, count);
};

const pickRandomItems = (items, count) => {
    if (!Array.isArray(items) || !items.length || count <= 0) {
        return [];
    }
    const copy = [...items];
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy.slice(0, Math.min(count, copy.length));
};

function HomePage() {
    const { t, language } = useLanguage();
    const twitchParent =
        typeof window !== 'undefined' && window.location.hostname
            ? window.location.hostname
            : 'localhost';
    const twitchEmbedUrl = `https://player.twitch.tv/?channel=maf_5&parent=${twitchParent}&autoplay=false&muted=true`;
    const highlightLinks = ['/tufting', '/cartes-pokemon-3d', '/faq'];
    const highlights = t('home.highlights').map((item, index) => ({
        ...item,
        link: highlightLinks[index]
    }));
    const heroStats = t('home.hero.stats');
    const [heroShots, setHeroShots] = useState([]);
    const [recentItems, setRecentItems] = useState([]);
    const [isProductsLoading, setIsProductsLoading] = useState(true);
    const [reviewsStatus, setReviewsStatus] = useState('loading');
    const [reviewsData, setReviewsData] = useState({
        averageRating: 0,
        totalReviews: 0,
        items: []
    });
    const [featuredReviews, setFeaturedReviews] = useState([]);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const fetchReviews = async () => {
            setReviewsStatus('loading');
            try {
                const response = await fetch(REVIEWS_ENDPOINT, {
                    signal: controller.signal
                });

                if (!response.ok) {
                    throw new Error('Failed to load reviews.');
                }

                const payload = await response.json();
                if (!isMounted) {
                    return;
                }

                setReviewsData({
                    averageRating: Number(payload.averageRating) || 0,
                    totalReviews: Number(payload.totalReviews) || 0,
                    items: payload.items || []
                });
                setFeaturedReviews(pickFeaturedReviews(payload.items || []));
                setReviewsStatus('ready');
            } catch (error) {
                if (!isMounted || error.name === 'AbortError') {
                    return;
                }
                setReviewsStatus('error');
            }
        };

        fetchReviews();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, []);

    useEffect(() => {
        let ignore = false;
        const fetchRecentProducts = async () => {
            try {
                const [rugsRes, cardsRes] = await Promise.all([
                    fetch(
                        `${PRODUCTS_PUBLIC_ENDPOINT}?category=tapis&limit=80&order=created_desc`
                    ),
                    fetch(
                        `${PRODUCTS_PUBLIC_ENDPOINT}?category=cartes&limit=80&order=created_desc`
                    )
                ]);
                if (!rugsRes.ok || !cardsRes.ok || ignore) {
                    return;
                }
                const [rugsPayload, cardsPayload] = await Promise.all([
                    rugsRes.json(),
                    cardsRes.json()
                ]);
                if (ignore) {
                    return;
                }
                const normalizeMediaUrl = (value) => {
                    const raw = String(value || '').trim();
                    if (
                        raw.includes('/product-media/') &&
                        !raw.includes('/.netlify/functions/product-media/')
                    ) {
                        return raw.replace(
                            '/product-media/',
                            '/.netlify/functions/product-media/'
                        );
                    }
                    return raw;
                };
                const dbRugs = (rugsPayload.items || []).map((item) => ({
                    id: item.slug || String(item.id),
                    title: item.title,
                    type: t('home.studio.types.tufting'),
                    link: '/tufting',
                    image: normalizeMediaUrl((item.media || [])[0] || ''),
                    fallback: normalizeMediaUrl((item.media || [])[0] || '')
                }));
                const dbCards = (cardsPayload.items || []).map((item) => ({
                    id: item.slug || String(item.id),
                    title: item.title,
                    type: t('home.studio.types.cards'),
                    link: '/cartes-pokemon-3d',
                    image: normalizeMediaUrl((item.media || [])[0] || ''),
                    fallback: normalizeMediaUrl((item.media || [])[0] || '')
                }));
                const recentRugs = dbRugs.filter((item) => item.image).slice(0, 2);
                const recentCards = dbCards
                    .filter((item) => item.image)
                    .slice(0, 2);
                const merged = [...recentRugs, ...recentCards];
                if (merged.length) {
                    setRecentItems(merged);
                }
                const nextHero = [
                    ...pickRandomItems(
                        dbRugs.filter((item) => item.image),
                        4
                    ).map((item, index) => ({
                        src: item.image,
                        fallback: item.fallback || item.image,
                        label: item.title,
                        className: HERO_SHOT_CLASSES[index]
                    })),
                    ...pickRandomItems(
                        dbCards.filter((item) => item.image),
                        2
                    ).map((item, index) => ({
                        src: item.image,
                        fallback: item.fallback || item.image,
                        label: item.title,
                        className: HERO_SHOT_CLASSES[index + 4]
                    }))
                ].filter((item) => item.src);
                if (nextHero.length) {
                    setHeroShots(nextHero);
                }
            } catch {
                if (!ignore) {
                    setHeroShots([
                        {
                            src: carapuceCard,
                            fallback: carapuceCardFallback,
                            label: t('home.heroShots.card3d'),
                            className: HERO_SHOT_CLASSES[4]
                        },
                        {
                            src: spectrumCard,
                            fallback: spectrumCardFallback,
                            label: t('home.heroShots.relief3d'),
                            className: HERO_SHOT_CLASSES[5]
                        }
                    ]);
                }
            } finally {
                if (!ignore) {
                    setIsProductsLoading(false);
                }
            }
        };
        fetchRecentProducts();
        return () => {
            ignore = true;
        };
    }, [t]);

    const reviewNumberFormatter = useMemo(
        () => buildNumberFormatter(language),
        [language]
    );
    const reviewAverageFormatter = useMemo(
        () => buildNumberFormatter(language, { maximumFractionDigits: 1 }),
        [language]
    );
    const averageLabel = t('home.reviews.average', {
        count: reviewNumberFormatter.format(reviewsData.totalReviews)
    });
    const averageRatingLabel = `${reviewAverageFormatter.format(
        reviewsData.averageRating
    )} / 5`;

    return (
        <div className='page'>
            <Header />
            <main>
                <section className='hero home-hero'>
                    <div className='container hero-grid'>
                        <div className='hero-copy'>
                            <p className='eyebrow'>{t('home.hero.eyebrow')}</p>
                            <h1>
                                {t('home.hero.title')}
                            </h1>
                            <p className='lead'>
                                {t('home.hero.lead')}
                            </p>
                            <div className='hero-actions'>
                                <Link className='button' to='/tufting'>
                                    <YarnIcon
                                        size={18}
                                        weight='regular'
                                        aria-hidden='true'
                                    />
                                    {t('home.hero.actions.tufting')}
                                </Link>
                                <Link
                                    className='button button-secondary'
                                    to='/cartes-pokemon-3d'
                                >
                                    <CardsThreeIcon
                                        size={18}
                                        weight='regular'
                                        aria-hidden='true'
                                    />
                                    {t('home.hero.actions.cards')}
                                </Link>
                            </div>
                            <div className='hero-stats'>
                                {heroStats.map((stat) => (
                                    <div key={stat.value}>
                                        <span className='stat-value'>
                                            {stat.value}
                                        </span>
                                        <span className='stat-label'>
                                            {stat.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className='hero-visual'>
                            <div className='hero-mosaic'>
                                {isProductsLoading
                                    ? HERO_SHOT_CLASSES.map((className, index) => (
                                          <div
                                              key={`hero-skeleton-${index}`}
                                              className={`${className} skeleton-block`}
                                          />
                                      ))
                                    : heroShots.map((shot) => (
                                          <div
                                              key={shot.label}
                                              className={shot.className}
                                          >
                                              <Picture
                                                  webpSrc={shot.src}
                                                  fallbackSrc={shot.fallback}
                                                  alt={shot.label}
                                                  className='picture-fill'
                                              />
                                          </div>
                                      ))}
                            </div>
                            <div className='hero-caption card'>
                                <span className='chip'>
                                    {t('home.heroCaption.chip')}
                                </span>
                                <h3>{t('home.heroCaption.title')}</h3>
                                <p>{t('home.heroCaption.description')}</p>
                                <Link className='text-link' to='/contact'>
                                    {t('home.heroCaption.link')}
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                <section className='section highlight-section'>
                    <div className='container highlight-grid'>
                        {highlights.map((item, index) => (
                            <div
                                key={`${item.title}-${index}`}
                                className='card highlight-card reveal'
                                style={{ '--delay': `${index * 0.15}s` }}
                            >
                                <h3>{item.title}</h3>
                                <p>{item.description}</p>
                                <Link className='text-link' to={item.link}>
                                    {item.cta}
                                </Link>
                            </div>
                        ))}
                    </div>
                </section>

                <section className='section'>
                    <div className='container section-header'>
                        <div>
                            <p className='eyebrow'>
                                {t('home.studio.eyebrow')}
                            </p>
                            <h2>{t('home.studio.title')}</h2>
                        </div>
                        <p className='section-note'>
                            {t('home.studio.note')}
                        </p>
                    </div>
                    <div className='container studio-grid'>
                        {isProductsLoading
                            ? Array.from({ length: 4 }).map((_, index) => (
                                  <div
                                      key={`studio-skeleton-${index}`}
                                      className='studio-card studio-card-skeleton'
                                  >
                                      <div className='skeleton-block studio-skeleton-image' />
                                      <div className='studio-skeleton-body'>
                                          <div className='skeleton-line skeleton-line-title' />
                                          <div className='skeleton-line skeleton-line-tag' />
                                      </div>
                                  </div>
                              ))
                            : recentItems.map((item, index) => (
                                  <Link
                                      key={item.id}
                                      className='studio-card reveal'
                                      style={{ '--delay': `${index * 0.12}s` }}
                                      to={item.link}
                                  >
                                      <Picture
                                          webpSrc={item.image}
                                          fallbackSrc={item.fallback}
                                          alt={item.title}
                                      />
                                      <span className='studio-title'>
                                          {item.title}
                                      </span>
                                      <span className='studio-tag'>
                                          {item.type}
                                      </span>
                                  </Link>
                              ))}
                    </div>
                </section>

                <section className='section twitch-section'>
                    <div className='container section-header'>
                        <div>
                            <p className='eyebrow'>
                                {t('home.twitch.eyebrow')}
                            </p>
                            <h2>{t('home.twitch.title')}</h2>
                        </div>
                        <p className='section-note'>
                            {t('home.twitch.note')}
                        </p>
                    </div>
                    <div className='container twitch-preview'>
                        <div className='card twitch-embed'>
                            <iframe
                                className='twitch-frame'
                                src={twitchEmbedUrl}
                                title='Twitch stream maf_5'
                                allowFullScreen
                                scrolling='no'
                            />
                        </div>
                        <div className='card twitch-copy'>
                            <h3>{t('home.twitch.cardTitle')}</h3>
                            <p>{t('home.twitch.cardNote')}</p>
                            <div className='twitch-actions'>
                                <a
                                    className='text-link'
                                    href='https://www.twitch.tv/maf_5'
                                    target='_blank'
                                    rel='noreferrer'
                                >
                                    {t('common.openChannel')}
                                </a>
                                <Link className='text-link' to='/contact'>
                                    {t('common.allLinks')}
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                <section className='section reviews-section'>
                    <div className='container'>
                        <div className='section-header reviews-header'>
                            <div>
                                <p className='eyebrow'>
                                    {t('home.reviews.eyebrow')}
                                </p>
                                <h2>{t('home.reviews.title')}</h2>
                            </div>
                            <div className='reviews-summary card'>
                                <span className='reviews-score'>
                                    {reviewAverageFormatter.format(
                                        reviewsData.averageRating
                                    )}
                                </span>
                                <StarRow
                                    value={reviewsData.averageRating}
                                    label={averageRatingLabel}
                                />
                                <span className='reviews-count'>
                                    {averageLabel}
                                </span>
                            </div>
                        </div>
                        <div className='reviews-grid'>
                            {featuredReviews.map((review) => (
                                <article
                                    key={review.id}
                                    className='card review-card'
                                >
                                    <div className='review-card-header'>
                                        <StarRow
                                            value={review.rating}
                                            label={`${review.rating} / 5`}
                                            size={14}
                                        />
                                        <span className='review-rating-value'>
                                            {review.rating}/5
                                        </span>
                                    </div>
                                    {review.title && (
                                        <h3>{review.title}</h3>
                                    )}
                                    <p>{review.comment}</p>
                                    <div className='review-card-footer'>
                                        <span className='review-name'>
                                            {review.name ||
                                                t('common.anonymous')}
                                        </span>
                                        {review.context && (
                                            <span className='review-tag'>
                                                {review.context}
                                            </span>
                                        )}
                                    </div>
                                </article>
                            ))}
                            {reviewsStatus === 'ready' &&
                                featuredReviews.length === 0 && (
                                    <p className='reviews-empty'>
                                        {t('home.reviews.empty')}
                                    </p>
                                )}
                            {reviewsStatus === 'error' && (
                                <p className='reviews-empty'>
                                    {t('home.reviews.empty')}
                                </p>
                            )}
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}

export default HomePage;
